import { ReactNode } from "react";
import { X } from "lucide-react";
import { Modal, ModalProps } from "./Modal";
import { Button, ButtonProps } from "./Button";
import { twMerge } from "tailwind-merge";

interface ModalDialogProps extends ModalProps {
  title: ReactNode;
  description?: ReactNode;
  bodyClassName?: string;
  footerLeftSlot?: ReactNode;
  cancelButtonProps?: ButtonProps;
  confirmButtonProps?: ButtonProps;
  isLoading?: boolean;
  hideFooter?: boolean;
  closeButtonDataTestId?: string;
  cancelButtonDataTestId?: string;
  confirmButtonDataTestId?: string;
}

export function ModalDialog({
  title,
  description,
  bodyClassName,
  closeButtonDataTestId,
  cancelButtonDataTestId,
  confirmButtonDataTestId,
  children,
  onClose,
  className,
  cancelButtonProps,
  confirmButtonProps,
  isLoading,
  hideFooter,
  footerLeftSlot,
  ...modalProps
}: ModalDialogProps) {
  const handleClose = isLoading ? () => {} : onClose;

  return (
    <Modal
      {...modalProps}
      onClose={handleClose}
      className={twMerge("p-0", className)}
    >
      <div className="flex w-[90vw] max-w-[560px] items-start gap-4 border-b border-[#E5E7EB] px-6 py-5">
        <div className="min-w-0 flex-1">
          <span className="text-[16px] font-bold text-[#101828]">{title}</span>
          {description && (
            <p className="mt-1 text-xs font-medium text-[#6A7282]">
              {description}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          color="secondary"
          icon={X}
          size="lg"
          className="rounded-[12px] text-[#6A7282]"
          aria-label="Close"
          onClick={handleClose}
          disabled={isLoading}
          data-testid={closeButtonDataTestId}
        />
      </div>

      <div className={twMerge("px-6 pt-5 pb-4.5", bodyClassName)}>
        {children}
      </div>

      {!hideFooter && (
        <div className="flex items-center justify-end gap-2 border-t border-[#EBEDF2] px-6 py-4">
          {footerLeftSlot && <div className="mr-auto">{footerLeftSlot}</div>}
          <Button
            variant="ghost"
            color="primary"
            size="lg"
            {...cancelButtonProps}
            className={twMerge(
              "px-4 py-2.5 font-semibold text-[#4A5565]",
              cancelButtonProps?.className,
            )}
            onClick={cancelButtonProps?.onClick ?? handleClose}
            disabled={isLoading || cancelButtonProps?.disabled}
            data-testid={cancelButtonDataTestId}
          >
            {cancelButtonProps?.children ?? "Cancel"}
          </Button>
          {confirmButtonProps !== undefined && (
            <Button
              variant="contained"
              color="primary"
              size="lg"
              {...confirmButtonProps}
              className={twMerge("px-5 py-2.5", confirmButtonProps?.className)}
              disabled={isLoading || confirmButtonProps.disabled}
              data-testid={confirmButtonDataTestId}
            >
              {confirmButtonProps.children ?? "Confirm"}
            </Button>
          )}
        </div>
      )}
    </Modal>
  );
}
